import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { PropertyResource } from 'src/types/api.v1.property';
import type { GPSMetadata } from 'src/composables/useGeolocation';
import type { CaptureMethod } from 'src/types/photo';

/**
 * Cross-step scratchpad for the onboarding Easy / Full flows (Block B
 * redesign, May 2026). Each step writes its own field; the final step
 * reads the resulting property and clears the store.
 *
 * Pre-1.0 we keep a single store covering both flows — the Full flow
 * fields stay `null` for an Easy run and vice versa. Splitting per
 * flow would add ceremony with no concrete win.
 *
 * Lifecycle:
 *   - User enters /onboarding/easy/photo → setPhoto()
 *   - User enters /onboarding/easy/nickname → setNickname() → submit() →
 *     setCreatedProperty()
 *   - User enters /onboarding/easy/done → reads createdProperty
 *   - User taps "Go to home" → reset() and route to /dashboard
 *
 * Photos kept here as native `File` objects (not blob URLs) so we can
 * re-upload if a retry is needed. The preview URL is the object URL
 * lifecycle-managed by the steps; revoking happens on `reset()`.
 */
export const useOnboardingFlowStore = defineStore('onboardingFlow', () => {
  // Easy + Full shared
  const photoFile = ref<File | null>(null);
  const photoGps = ref<GPSMetadata | null>(null);
  const photoCaptureMethod = ref<CaptureMethod>('uploaded');
  const photoPreviewUrl = ref<string | null>(null);
  const nickname = ref('');
  const createdProperty = ref<PropertyResource | null>(null);
  const isSubmitting = ref(false);

  // Full flow only. `userTypeTag` is the user's intent label (my-home,
  // rental, second-place, investment, other-type) — recorded as a
  // property tag for now since the API doesn't yet have a dedicated
  // user-relationship-style field for residential sub-types.
  const userTypeTag = ref<string | null>(null);
  const address = ref<{
    street?: string;
    city?: string;
    country?: string;
    year_built?: number;
  } | null>(null);
  const firstTaskTitle = ref<string | null>(null);

  function setPhoto(file: File, gps?: GPSMetadata | null, captureMethod: CaptureMethod = 'uploaded'): void {
    if (photoPreviewUrl.value) URL.revokeObjectURL(photoPreviewUrl.value);
    photoFile.value = file;
    photoGps.value = gps ?? null;
    photoCaptureMethod.value = captureMethod;
    photoPreviewUrl.value = URL.createObjectURL(file);
  }

  function clearPhoto(): void {
    if (photoPreviewUrl.value) URL.revokeObjectURL(photoPreviewUrl.value);
    photoFile.value = null;
    photoGps.value = null;
    photoCaptureMethod.value = 'uploaded';
    photoPreviewUrl.value = null;
  }

  function setNickname(name: string): void {
    nickname.value = name.trim();
  }

  function setCreatedProperty(property: PropertyResource): void {
    createdProperty.value = property;
  }

  function setSubmitting(value: boolean): void {
    isSubmitting.value = value;
  }

  function setUserTypeTag(tag: string): void {
    userTypeTag.value = tag;
  }

  function setAddress(value: {
    street?: string;
    city?: string;
    country?: string;
    year_built?: number;
  } | null): void {
    address.value = value;
  }

  function setFirstTaskTitle(title: string | null): void {
    firstTaskTitle.value = title?.trim() || null;
  }

  function reset(): void {
    if (photoPreviewUrl.value) URL.revokeObjectURL(photoPreviewUrl.value);
    photoFile.value = null;
    photoGps.value = null;
    photoCaptureMethod.value = 'uploaded';
    photoPreviewUrl.value = null;
    nickname.value = '';
    createdProperty.value = null;
    isSubmitting.value = false;
    userTypeTag.value = null;
    address.value = null;
    firstTaskTitle.value = null;
  }

  return {
    photoFile,
    photoGps,
    photoCaptureMethod,
    photoPreviewUrl,
    nickname,
    createdProperty,
    isSubmitting,
    userTypeTag,
    address,
    firstTaskTitle,
    setPhoto,
    clearPhoto,
    setNickname,
    setCreatedProperty,
    setSubmitting,
    setUserTypeTag,
    setAddress,
    setFirstTaskTitle,
    reset,
  };
});
